import { useCallback, useEffect, useMemo, useState } from 'react';
import { createContext, useContext } from 'use-context-selector';

import { PINNING_STEP, usePinningStepContext } from './PinningStepProvider';

const initialContextValues = {
  venueName: '',
  optionsOpen: false,
  isTyping: false,
  handleSetIsTyping: (_value: boolean) => {
    return;
  },
  handleSetInitialFocus: () => {
    return;
  },
  onInputValueChange: (_value: string) => {
    return;
  },
  isVenueNotExisted: (_itemsSize: number) => {
    return false;
  },
  enabledVenueCreation: () => {
    return false;
  },
};

const StartAndFindVenueContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
};

export const StartAndFindVenueProvider: React.FC<Props> = ({ children }) => {
  const { pinningStep, handleSetPinningStep } = usePinningStepContext();

  const [venueName, setVenueName] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);

  const handleSetIsTyping = useCallback((value: boolean) => {
    setIsTyping(value);
  }, []);

  const handleSetInitialFocus = useCallback(() => {
    if (!venueName && !isTyping) handleSetPinningStep(PINNING_STEP.FIND_VENUE);
  }, [handleSetPinningStep, isTyping, venueName]);

  const onInputValueChange = useCallback(
    (value: string) => {
      setIsTyping(true);
      setVenueName(value.trim());
      setOptionsOpen(!!value.trim());

      if (!value.trim()) handleSetPinningStep(PINNING_STEP.START);
      else handleSetPinningStep(PINNING_STEP.FIND_VENUE);
    },
    [handleSetPinningStep],
  );

  const isVenueNotExisted = useCallback(
    (itemsSize: number): boolean => {
      return (
        pinningStep === PINNING_STEP.FIND_VENUE && !isTyping && itemsSize === 0
      );
    },
    [isTyping, pinningStep],
  );

  const enabledVenueCreation = useCallback((): boolean => {
    return pinningStep === PINNING_STEP.FIND_VENUE && !!venueName;
  }, [pinningStep, venueName]);

  useEffect(() => {
    // This effect is triggered
    // when the user clicks the back icon to navigate back to the home screen.
    if (pinningStep === PINNING_STEP.START) {
      setVenueName('');
      setOptionsOpen(false);
      setIsTyping(false);
    }
  }, [pinningStep]);

  const providerValues = useMemo(
    () => ({
      venueName,
      handleSetIsTyping,
      optionsOpen,
      handleSetInitialFocus,
      onInputValueChange,
      isVenueNotExisted,
      enabledVenueCreation,
      isTyping,
    }),
    [
      venueName,
      handleSetIsTyping,
      optionsOpen,
      handleSetInitialFocus,
      onInputValueChange,
      isVenueNotExisted,
      enabledVenueCreation,
      isTyping,
    ],
  );

  return (
    <StartAndFindVenueContext.Provider value={providerValues}>
      {children}
    </StartAndFindVenueContext.Provider>
  );
};

export const useStartAndFindVenueContext = () =>
  useContext(StartAndFindVenueContext);
